'use client';

import { useState } from 'react';
import { getMediaData } from '@/lib/utils/media.utils';
import type { Media } from '@/types';
import { SzMedia } from './skin-zero-ui';

export function SkinZeroEmbed({ block }: { block: Record<string, unknown> }) {
  const [active, setActive] = useState(false);
  const title = typeof block.title === 'string' ? block.title : 'Video';
  let href: string | null = null;
  let src: string | null = null;
  try {
    const url = new URL(String(block.url));
    if (['http:', 'https:'].includes(url.protocol)) href = url.href;
    const host = url.hostname.replace(/^www\./, '');
    if (block.provider === 'youtube' && ['youtube.com', 'youtu.be'].includes(host)) {
      const id = host === 'youtu.be' ? url.pathname.slice(1) : url.searchParams.get('v');
      if (id && /^[\w-]+$/.test(id)) src = `https://www.youtube-nocookie.com/embed/${id}?autoplay=1`;
    }
    if (block.provider === 'vimeo' && host === 'vimeo.com' && /^\/\d+$/.test(url.pathname))
      src = `https://player.vimeo.com/video${url.pathname}?autoplay=1`;
  } catch {
    /* Invalid provider addresses are never embedded. */
  }
  const poster = getMediaData(block.poster as Media | null).url;
  return (
    <section className="space-y-4" data-template-view="skin-zero-embed">
      <h2 className="text-2xl font-semibold tracking-tight">{title}</h2>
      {active && src ? (
        <iframe
          src={src}
          title={title}
          allow="autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
          className="aspect-video w-full rounded-lg border border-neutral-200 dark:border-neutral-800"
        />
      ) : (
        <div className="space-y-4">
          {poster && <SzMedia url={poster} alt={title} layout="intrinsic" />}
          {src ? (
            <button
              onClick={() => setActive(true)}
              className="rounded-lg border border-neutral-300 px-4 py-2 text-sm dark:border-neutral-700"
            >
              Play video
            </button>
          ) : (
            href && (
              <a href={href} target="_blank" rel="noopener noreferrer" className="text-sm underline">
                Open {block.provider === 'x' ? 'post on X' : 'source'} ↗
              </a>
            )
          )}
        </div>
      )}
      {typeof block.context === 'string' && <p className="max-w-3xl text-base leading-relaxed">{block.context}</p>}
      {typeof block.attribution === 'string' && <p className="text-sm text-neutral-500">{block.attribution}</p>}
    </section>
  );
}
